/**
 * "Winning Hands" guide — every winning-hand name with a sample hand and its
 * faan value, as a read-only pop-up reachable from both the main device
 * (`play.ts`) and a guest device (`guestPlay.ts`). The content is the same
 * material as the four MOJO brochures (Know Your Tiles, Hand Faan, Limit Hand,
 * Additional Faan), so players see the same names and
 * pictures at the table and on the wall.
 *
 * Nothing here is remembered separately from the rules: fan values are read
 * from the game's own settings (`settings.fan`, `activeFanCap`) and a bonus
 * whose settings toggle is off is marked "Off at this table" — a brochure is
 * printed once, but every venue can tune these values. Limit hands show the
 * table's fan cap for Great Winds / All Kongs / Thirteen Orphans, which is what
 * the engine pays for them; All Honour Tiles, Self Triplets, Orphans and Nine
 * Gates are fixed-value limit hands (10 faan by default, from `settings.fan`) —
 * see `detectLimitHands` in rules/hkos/patterns.ts. tests/ui/handGuide.test.ts
 * runs every sample hand through the real scoring engine, so a sample can't
 * quietly stop scoring as the pattern it is named after.
 *
 * Like every other overlay in the app it holds no game state and never
 * touches scoring — opening it is always safe mid-round.
 */

import { activeFanCap, type FanValues, type HkosSettings, type HkosToggles } from '../rules/hkos';
import { parseTiles } from '../rules/tiles';
import { esc } from './helpers';

export type GuideTab = 'basics' | 'hand' | 'limit' | 'additional';

export interface GuideItem {
  /** Matches PATTERN_GLOSSARY / PatternHit.key. Absent for Chicken Hand, which is the *absence* of a pattern. */
  key?: string;
  name: string;
  /** What the round breakdown calls it, when that differs from the name printed in the brochure. */
  alias?: string;
  /**
   * Tile groups drawn left to right: '123m' (compact notation), or bonus tiles
   * written out ('F1F2F3F4', 'S1S2S3S4'). An empty list draws no tiles.
   */
  groups: string[];
  description: string;
  /** Where the faan number comes from. Absent with `fixedFan`, or for a limit hand. */
  fanKey?: keyof FanValues;
  fixedFan?: number;
  limit?: boolean;
  /** The settings toggle that switches this on. Absent when it always applies. */
  toggle?: keyof HkosToggles;
  /** Four-tile groups are kongs (All Kongs) — only matters to the engine test, never to the picture. */
  kongs?: boolean;
}

interface GuideSection {
  title: string;
  note?: (settings: HkosSettings) => string;
  items: GuideItem[];
}

/** Brochure 1's valid-hand panel: what every winning hand has to look like first. */
export const BASIC_SHAPE: { label: string; group: string }[] = [
  { label: 'Chow', group: '123p' },
  { label: 'Pung', group: '888m' },
  { label: 'Pung', group: '222z' },
  { label: 'Chow', group: '678s' },
  { label: 'Pair', group: '77z' },
];

/** Brochure 1's tile guide — the 27 suit tiles, then honours, then bonus tiles. */
export interface TileGroup {
  label: string;
  /** [tile code, caption printed under it] */
  tiles: [string, string][];
}

export const SUIT_ROWS: { label: string; suit: 'm' | 's' | 'p' }[] = [
  { label: 'Symbols (Wan)', suit: 'm' },
  { label: 'Bamboos (Tiao)', suit: 's' },
  { label: 'Dots (Tong)', suit: 'p' },
];

export const HONOR_GROUPS: TileGroup[] = [
  { label: 'Winds', tiles: [['1z', 'East'], ['2z', 'South'], ['3z', 'West'], ['4z', 'North']] },
  { label: 'Dragons', tiles: [['5z', 'White'], ['6z', 'Green'], ['7z', 'Red']] },
];

export const BONUS_GROUPS: TileGroup[] = [
  { label: 'Flowers', tiles: [['F1', 'Plum'], ['F2', 'Orchid'], ['F3', 'Chrysant.'], ['F4', 'Bamboo']] },
  { label: 'Seasons', tiles: [['S1', 'Spring'], ['S2', 'Summer'], ['S3', 'Autumn'], ['S4', 'Winter']] },
];

/** Brochure 1's second heading, over the sample valid hand. */
export const VALID_HAND_INTRO = {
  eyebrow: 'How a hand wins',
  heading: 'Winning Hand Patterns',
  intro:
    "Every hand must first be a valid shape — 4 sets (chow, pung, or kong) + 1 pair. Here's a sample valid hand.",
};

/** Brochure 2 — patterns that add faan on top of the base shape. */
export const HAND_FAAN: GuideItem[] = [
  {
    name: 'Chicken Hand',
    groups: ['123m', '567p', '789s', '444m', '99p'],
    description:
      'No bonus pattern at all — just the base 4-sets-and-a-pair shape. The floor value before any faan is added.',
    fixedFan: 0,
  },
  {
    key: 'allSequences',
    name: 'Common Hand',
    alias: 'All Sequences',
    groups: ['123m', '456m', '123p', '123s', '99m'],
    description: 'Every set is a chow (run) — no pungs or kongs at all.',
    fanKey: 'allSequences',
  },
  {
    key: 'allPungs',
    name: 'All In Triplets',
    alias: 'All Pungs',
    groups: ['222m', '555p', '777s', '444m', '99p'],
    description: 'Every set is a pung or kong — no chows at all.',
    fanKey: 'allPungs',
  },
  {
    key: 'halfFlush',
    name: 'Mixed One Suit',
    alias: 'Half Flush',
    groups: ['234p', '567p', '888p', '222z', '99p'],
    description: 'One numbered suit plus honour tiles — no mixing numbered suits.',
    fanKey: 'halfFlush',
  },
  {
    key: 'sevenPairs',
    name: 'Seven Pairs',
    groups: ['22m', '55p', '77s', '11z', '66z', '33m', '88p'],
    description: 'Seven pairs, any suits or ranks — not 4-sets-and-a-pair.',
    fanKey: 'sevenPairs',
  },
  {
    key: 'fullFlush',
    name: 'All One Suit',
    alias: 'Full Flush',
    groups: ['123s', '456s', '789s', '333s', '22s'],
    description: 'One numbered suit only — no honours, no mixing.',
    fanKey: 'fullFlush',
  },
  {
    key: 'mixedOrphans',
    name: 'Mixed Orphans',
    groups: ['111m', '999p', '111z', '555z', '99s'],
    description:
      'Every set is a pung/kong of a 1, 9, or honour tile — with at least one of each. Implies All In Triplets, which adds another 3 faan.',
    fanKey: 'mixedOrphans',
  },
  {
    key: 'smallDragons',
    name: 'Small Dragons',
    groups: ['555z', '666z', '123s', '456p', '77z'],
    description:
      "A pung (or kong) of two dragon tiles, plus a pair of the third. This is the pattern's entire dragon faan — it doesn't stack with Dragon Pung or All In Triplets.",
    fanKey: 'smallDragons',
  },
  {
    key: 'greatDragons',
    name: 'Great Dragons',
    groups: ['555z', '666z', '777z', '345m', '99p'],
    description:
      'A pung (or kong) of all three dragon tiles — White, Green, and Red. Replaces Dragon Pung and All In Triplets rather than stacking with them.',
    fanKey: 'greatDragons',
  },
  {
    key: 'smallWinds',
    name: 'Small Winds',
    groups: ['111z', '222z', '333z', '456p', '44z'],
    description:
      'A pung (or kong) of three wind tiles, plus a pair of the fourth. Replaces All In Triplets if that would also apply — but still stacks with Mixed One Suit. A dragon as the 4th meld instead makes the whole hand All Honour Tiles, a limit hand, which wins outright.',
    fanKey: 'smallWinds',
  },
];

/** Brochure 3 — the limit hands: 10 faan for the first four, the table cap for the last three. */
export const LIMIT_HANDS: GuideItem[] = [
  {
    key: 'allHonours',
    name: 'All Honour Tiles',
    alias: 'All Honours',
    groups: ['222z', '333z', '444z', '666z', '55z'],
    description: 'Every tile in the hand is a wind or a dragon — no numbered tiles at all.',
    fanKey: 'allHonours',
  },
  {
    key: 'selfTriplets',
    name: 'Self Triplets',
    groups: ['222m', '555p', '777s', '333z', '88m'],
    description:
      'Every set is a concealed pung or kong. Won by self-draw, or by a discard that completes the pair — not one of the triplets.',
    fanKey: 'selfTriplets',
  },
  {
    key: 'orphans',
    name: 'Orphans',
    groups: ['111m', '999m', '111p', '999p', '11s'],
    description: 'Every tile is a 1 or a 9 — no honours, no other numbers.',
    fanKey: 'orphans',
  },
  {
    key: 'nineGates',
    name: 'Nine Gates',
    groups: ['111p', '234567p', '8p', '9999p'],
    description: 'Concealed, one suit only: 1112345678999 plus one more tile of that same suit.',
    fanKey: 'nineGates',
  },
  {
    key: 'greatWinds',
    name: 'Great Winds',
    groups: ['111z', '222z', '333z', '444z', '55p'],
    description: 'A pung or kong of all four wind tiles — East, South, West, and North.',
    limit: true,
  },
  {
    key: 'allKongs',
    name: 'All Kongs',
    groups: ['1111m', '9999p', '4444z', '7777z', '99s'],
    description: 'Hand containing four kongs, plus a pair.',
    limit: true,
    kongs: true,
  },
  {
    key: 'thirteenOrphans',
    name: 'Thirteen Orphans',
    groups: ['19m', '19p', '19s', '1234z', '567z', '1z'],
    description:
      'One of each terminal and honour tile — 1 and 9 of every suit, all four winds, all three dragons — plus one more of any of them as the pair.',
    limit: true,
  },
];

/** Brochure 4 — bonuses that stack on top of a hand's own pattern. */
export const ADDITIONAL_SECTIONS: GuideSection[] = [
  {
    title: 'Wind & Dragon Faan',
    note: (settings) => {
      const { fan, toggles } = settings;
      const both = toggles.seatWindPung && toggles.prevailingWindPung;
      return (
        'A wind pung that is both your seat wind and the prevailing wind earns both bonuses at once' +
        (both ? ` — ${fan.seatWindPung + fan.prevailingWindPung} faan combined.` : '.')
      );
    },
    items: [
      {
        key: 'seatWindPung',
        name: 'Seat Wind Pung',
        groups: ['111z'],
        description: 'A pung or kong of your own seat wind.',
        fanKey: 'seatWindPung',
        toggle: 'seatWindPung',
      },
      {
        key: 'prevailingWindPung',
        name: 'Round Wind',
        alias: 'Prevailing Wind Pung',
        groups: ['222z'],
        description: "A pung or kong of the round's prevailing wind.",
        fanKey: 'prevailingWindPung',
        toggle: 'prevailingWindPung',
      },
      {
        key: 'dragonPung',
        name: 'Dragon Pung',
        groups: ['666z'],
        description: 'A pung or kong of any one dragon — White, Green, or Red.',
        fanKey: 'dragonPung',
      },
    ],
  },
  {
    title: 'Flower Faan',
    items: [
      {
        key: 'flowers',
        name: 'Flower / Season Tile',
        groups: ['F1S3'],
        description: 'Every flower or season tile you hold — 1 faan apiece.',
        fanKey: 'flower',
        toggle: 'flowerBonus',
      },
      {
        key: 'seatFlowerBonus',
        name: 'Flower of Your Wind',
        alias: 'Seat Flower',
        groups: ['F1'],
        description:
          "Extra faan when a flower or season's printed number matches your seat wind. Stacks per matching tile.",
        fanKey: 'seatFlowerBonus',
        toggle: 'seatFlowerBonus',
      },
      {
        key: 'allFlowers',
        name: 'All Flowers',
        groups: ['F1F2F3F4'],
        description: 'Holding all 4 flower tiles. Does not stack with Flower of Your Wind for the matching one.',
        fanKey: 'allFlowers',
        toggle: 'allFlowers',
      },
      {
        key: 'allSeasons',
        name: 'All Seasons',
        groups: ['S1S2S3S4'],
        description: 'Holding all 4 season tiles. Does not stack with Flower of Your Wind for the matching one.',
        fanKey: 'allSeasons',
        toggle: 'allSeasons',
      },
      {
        key: 'noFlowerBonus',
        name: 'No Flowers or Seasons',
        alias: 'No Flowers',
        groups: [],
        description: 'A clean hand holding zero bonus tiles at all. Never combined with the flower bonus above.',
        fanKey: 'noFlowerBonus',
        toggle: 'noFlowerBonus',
      },
    ],
  },
  {
    title: 'Winning Condition Faan',
    items: [
      {
        key: 'selfDraw',
        name: 'Self-Draw',
        groups: [],
        description: 'The winning tile is drawn from the wall, not discarded.',
        fanKey: 'selfDraw',
      },
      {
        key: 'concealedHand',
        name: 'Concealed Hand',
        alias: 'Fully Concealed Hand',
        groups: [],
        description: 'No melds exposed — the entire hand stayed hidden until the win.',
        fanKey: 'concealedHand',
      },
      {
        key: 'robbingKong',
        name: 'Robbing the Kong',
        alias: 'Robbing a Kong',
        groups: [],
        description: 'Winning off the very tile an opponent just used to declare a kong.',
        fanKey: 'robbingKong',
        toggle: 'robbingKong',
      },
      {
        key: 'kongReplacement',
        name: 'Kong Replacement',
        alias: 'Kong Replacement Win',
        groups: [],
        description: 'The winning tile is the replacement draw after declaring a kong.',
        fanKey: 'kongReplacement',
        toggle: 'kongReplacement',
      },
      {
        key: 'lastWallTile',
        name: 'Last Wall Tile',
        groups: [],
        description: 'A self-draw win on the very last tile of the wall.',
        fanKey: 'lastWallTile',
        toggle: 'lastWallTile',
      },
      {
        key: 'lastDiscard',
        name: 'Last Discard',
        groups: [],
        description: 'A discard win on the very last discard of the game, before the wall runs out.',
        fanKey: 'lastDiscard',
        toggle: 'lastDiscard',
      },
      {
        key: 'winDoubleKong',
        name: 'Win by Double Kong',
        alias: 'Win Double Kong',
        groups: [],
        description: "A self-drawn kong completed from an earlier kong's own replacement tile.",
        fanKey: 'winDoubleKong',
        toggle: 'winDoubleKong',
      },
      {
        key: 'heavenlyHand',
        name: 'Heavenly Hand',
        groups: [],
        description: 'The dealer wins outright with their original, untouched 14 tiles.',
        limit: true,
        toggle: 'heavenlyHand',
      },
      {
        key: 'earthlyHand',
        name: 'Earthly Hand',
        groups: [],
        description: "A non-dealer wins by claiming the dealer's very first discard.",
        limit: true,
        toggle: 'earthlyHand',
      },
    ],
  },
];

/** The lead-in each brochure prints under its header — verbatim, so the app and the wall poster read the same. */
export const TAB_INTROS: Record<GuideTab, { eyebrow?: string; heading: string; intro?: string }> = {
  basics: { heading: 'Know Your Tiles' },
  hand: {
    eyebrow: 'How a hand wins',
    heading: 'Winning Hand Patterns',
    intro:
      'Every hand must first be a valid shape — 4 sets (chow, pung, or kong) + 1 pair. The patterns below are what add faan on top of this base shape.',
  },
  limit: {
    eyebrow: 'The highest hands',
    heading: 'Limit Hand Patterns',
    intro:
      "These hands are so rare they pay the table's maximum faan on their own — ineligible for any additional faan from winds, dragons, or flowers.",
  },
  additional: {
    eyebrow: 'Beyond the base hand',
    heading: 'Additional Faan',
    intro:
      "These bonuses stack on top of your hand's own pattern faan — awarded for the tiles you hold and the way you won the round.",
  },
};

export const TAB_LABELS: Record<GuideTab, string> = {
  basics: 'Basics',
  hand: 'Hand Faan',
  limit: 'Limit Hands',
  additional: 'Additional Faan',
};

/** One drawn tile group → its tile codes, in order. */
export function expandGroup(group: string): string[] {
  return /^[FS]/.test(group) ? (group.match(/[FS][1-4]/g) ?? []) : parseTiles(group);
}

/** Every item of the three tabs, in display order — the engine test walks this. */
export function allGuideItems(): GuideItem[] {
  return [...HAND_FAAN, ...LIMIT_HANDS, ...ADDITIONAL_SECTIONS.flatMap((s) => s.items)];
}

/** "3 faan", "Limit · 13 faan" — what this game pays for the item. */
export function guideFanLabel(item: GuideItem, settings: HkosSettings): string {
  if (item.limit) return `Limit · ${activeFanCap(settings)} faan`;
  const value = item.fixedFan ?? settings.fan[item.fanKey!];
  return `${value} faan`;
}

/** True when the game's settings switch this bonus off, so it never scores here. */
export function isOffAtThisTable(item: GuideItem, settings: HkosSettings): boolean {
  return item.toggle !== undefined && !settings.toggles[item.toggle];
}

function tileImg(code: string): string {
  return `<img class="guide-tile" src="/tile-icons/${esc(code)}.png" alt="${esc(code)}" />`;
}

function groupsHtml(groups: string[]): string {
  if (groups.length === 0) return '';
  const rendered = groups
    .map((g) => `<span class="guide-group">${expandGroup(g).map(tileImg).join('')}</span>`)
    .join('');
  return `<div class="guide-tiles">${rendered}</div>`;
}

function itemHtml(item: GuideItem, settings: HkosSettings): string {
  const off = isOffAtThisTable(item, settings);
  return `
    <div class="guide-item${off ? ' is-off' : ''}" data-guide-item="${esc(item.key ?? 'chickenHand')}">
      ${groupsHtml(item.groups)}
      <div class="guide-head">
        <span class="guide-name">${esc(item.name)}</span>
        <span class="tag guide-fan">${esc(guideFanLabel(item, settings))}</span>
        ${off ? '<span class="tag guide-off">Off at this table</span>' : ''}
      </div>
      ${item.alias ? `<div class="guide-alias">Shown on scores as “${esc(item.alias)}”</div>` : ''}
      <p class="guide-desc">${esc(item.description)}</p>
    </div>`;
}

function captionedTile(code: string, caption: string): string {
  return `<div class="guide-cap-tile">${tileImg(code)}<span class="guide-cap">${esc(caption)}</span></div>`;
}

/** Honours side by side (winds are 4 wide, dragons 3), bonus tiles stacked (their names are long). */
function tileGroupsHtml(groups: TileGroup[], stacked: boolean): string {
  const body = groups
    .map(
      (g) => `
      <div class="guide-tile-group" style="flex:${g.tiles.length}">
        <div class="guide-row-label">${esc(g.label)}</div>
        <div class="guide-tile-grid" style="grid-template-columns:repeat(${g.tiles.length},1fr)">
          ${g.tiles.map(([code, caption]) => captionedTile(code, caption)).join('')}
        </div>
      </div>`,
    )
    .join('');
  return `<div class="guide-tile-groups${stacked ? ' is-stacked' : ''}">${body}</div>`;
}

/** Brochure 1's "Know Your Tiles" panel: suit tiles by number, then honours, then bonus tiles. */
function tileGuideHtml(): string {
  const ranks = Array.from({ length: 9 }, (_, i) => i + 1);
  // The 1-9 header goes straight above the first row of tiles; the other rows share its columns.
  const numbers = `<div class="guide-tile-grid guide-cols-9 guide-numbers">${ranks.map((r) => `<span>${r}</span>`).join('')}</div>`;
  const suitRows = SUIT_ROWS.map(
    (row, i) => `
      <div class="guide-suit-row">
        <div class="guide-row-label">${esc(row.label)}</div>
        ${i === 0 ? numbers : ''}
        <div class="guide-tile-grid guide-cols-9">${ranks.map((r) => tileImg(`${r}${row.suit}`)).join('')}</div>
      </div>`,
  ).join('');
  return `
    <div class="guide-tile-card">
      <div class="guide-group-heading"><span>Suit Tiles</span></div>
      ${suitRows}
      <div class="guide-group-heading"><span>Honor Tiles</span></div>
      ${tileGroupsHtml(HONOR_GROUPS, false)}
      <div class="guide-group-heading"><span>Bonus Tiles</span></div>
      ${tileGroupsHtml(BONUS_GROUPS, true)}
    </div>`;
}

/** Brochure 1's sample valid hand: chow + pung + pung + chow + pair. */
function validHandHtml(): string {
  const sets = BASIC_SHAPE.map(
    (s, i) => `
      <div class="guide-set">
        <div class="guide-set-body">
          <span class="guide-group">${expandGroup(s.group).map(tileImg).join('')}</span>
          <span class="guide-set-label">${esc(s.label)}</span>
        </div>
        ${i < BASIC_SHAPE.length - 1 ? '<span class="guide-plus">+</span>' : ''}
      </div>`,
  ).join('');
  return `
    <div class="guide-basic">
      <div class="guide-tiles guide-basic-sets">${sets}</div>
      <div class="guide-valid">= 14 tiles, a valid winning hand</div>
    </div>`;
}

/** A tab's heading block: whichever of eyebrow / heading / intro the brochure prints. */
function leadHtml(lead: { eyebrow?: string; heading: string; intro?: string }): string {
  return `
    <div class="guide-lead">
      ${lead.eyebrow ? `<div class="guide-eyebrow">${esc(lead.eyebrow)}</div>` : ''}
      <h2 class="guide-heading">${esc(lead.heading)}</h2>
      ${lead.intro ? `<p class="guide-desc guide-intro">${esc(lead.intro)}</p>` : ''}
    </div>`;
}

/** The scrollable body of one tab. Pure: same inputs, same HTML. */
export function guideTabHtml(tab: GuideTab, settings: HkosSettings): string {
  const lead = leadHtml(TAB_INTROS[tab]);
  if (tab === 'basics') {
    return lead + tileGuideHtml() + leadHtml(VALID_HAND_INTRO) + validHandHtml();
  }
  if (tab === 'hand') {
    return lead + HAND_FAAN.map((i) => itemHtml(i, settings)).join('');
  }
  if (tab === 'limit') {
    return lead + LIMIT_HANDS.map((i) => itemHtml(i, settings)).join('');
  }
  return (
    lead +
    ADDITIONAL_SECTIONS.map(
      (section) => `
      <h3 class="guide-section-title">${esc(section.title)}</h3>
      ${section.items.map((i) => itemHtml(i, settings)).join('')}
      ${section.note ? `<p class="guide-note">${esc(section.note(settings))}</p>` : ''}`,
    ).join('')
  );
}

let overlay: HTMLDivElement | undefined;

function close(): void {
  overlay?.remove();
  overlay = undefined;
  document.removeEventListener('keydown', onKeydown);
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') close();
}

/**
 * Opens the guide over the current screen. `settings` is the running game's
 * (the guest device gets it from the shared state), so values and "off at
 * this table" reflect the rules actually in play.
 */
export function openHandGuide(settings: HkosSettings): void {
  close();

  const el = document.createElement('div');
  el.className = 'lightbox-overlay';
  el.innerHTML = `
    <div class="card modal-panel guide-panel" role="dialog" aria-modal="true" aria-label="Winning hands">
      <div class="guide-top">
        <h1 style="margin:0">Winning Hands</h1>
        <button type="button" class="ghost guide-close" id="guideCloseBtn" aria-label="Close">Close</button>
      </div>
      <div class="guide-tabs" role="tablist">
        ${(Object.keys(TAB_LABELS) as GuideTab[])
          .map(
            (t) =>
              `<button type="button" role="tab" class="secondary guide-tab" data-tab="${t}">${esc(TAB_LABELS[t])}</button>`,
          )
          .join('')}
      </div>
      <div class="guide-body" id="guideBody"></div>
    </div>
  `;
  // Tapping the backdrop closes it; tapping inside the panel shouldn't.
  el.addEventListener('click', close);
  el.querySelector('.modal-panel')!.addEventListener('click', (e) => e.stopPropagation());
  el.querySelector('#guideCloseBtn')!.addEventListener('click', close);
  document.addEventListener('keydown', onKeydown);

  const body = el.querySelector<HTMLDivElement>('#guideBody')!;
  const tabs = [...el.querySelectorAll<HTMLButtonElement>('.guide-tab')];

  function show(tab: GuideTab): void {
    body.innerHTML = guideTabHtml(tab, settings);
    body.scrollTop = 0;
    for (const btn of tabs) {
      const selected = btn.dataset['tab'] === tab;
      btn.classList.toggle('is-selected', selected);
      btn.setAttribute('aria-selected', String(selected));
    }
  }
  for (const btn of tabs) {
    btn.addEventListener('click', () => show(btn.dataset['tab'] as GuideTab));
  }

  document.body.appendChild(el);
  overlay = el;
  show('basics');
}
